import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Box,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import { featureCollection, polygon } from "@turf/helpers";
import union from "@turf/union";
import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { TabPanel } from "../../../components/TabPanel";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import {
  BayDefinition,
  BayOrientation,
  GarageDoor,
  Hangar,
} from "../../../types";
import { formatUnit } from "../../../utils";
import { MetricAwareTextField } from "../../../widgets/MetricAwareTextField/MetricAwareTextField";
import { calculateGeometryArea } from "../../Ramp/RampInfoCard";
import { HangarBayEditor } from "./HangarBayEditor";
import { ObstacleToolbar } from "./ObstacleCanvas";

type Props = {
  localHangar: Hangar | null | undefined;
  setLocalHangar: (hangar: Hangar) => void;
  setIGarageDoor: (idx: number, garageDoor: GarageDoor) => void;
  hideGarageDoors?: boolean;
  activeTab: number;
  // Bay configuration (exposed to parent for persistence)
  bayDefinitions?: BayDefinition[];
  bayOrientation?: BayOrientation;
  useBayHeights?: boolean;
  onBayConfigChange?: (config: {
    useBayHeights: boolean;
    bayDefinitions: BayDefinition[];
    bayOrientation: BayOrientation;
  }) => void;
};

export const HangarDimensionsForm: React.FC<Props> = ({
  localHangar,
  setLocalHangar,
  hideGarageDoors = false,
  setIGarageDoor,
  activeTab,
  bayDefinitions: externalBayDefinitions,
  bayOrientation: externalBayOrientation,
  useBayHeights: externalUseBayHeights,
  onBayConfigChange,
}) => {
  const { activeFBO } = useActiveFBOs();
  const unit = activeFBO?.settings?.useMetricSystem ? "m" : "ft";
  const step = activeFBO?.settings?.useMetricSystem ? 0.1 : 0.5;

  // Bay configuration - fully controlled by parent
  const useBayHeights = externalUseBayHeights ?? false;
  const bayDefinitions = externalBayDefinitions ?? [];
  const bayOrientation = externalBayOrientation ?? "lateral";

  // Don't create wrapper setters - just call onBayConfigChange directly in the JSX
  // This avoids stale closure issues

  const usableSquareFootage = React.useMemo(() => {
    if (!localHangar) {
      return 0;
    }

    const grossSquareFootage = localHangar?.width * localHangar?.depth;

    if (!activeFBO?.settings?.ignoreDeadSpaceForUtilization) {
      return grossSquareFootage;
    }

    try {
      const obstaclePolygons = localHangar?.obstacles?.map((obstacle) => {
        const p = polygon([
          [
            [obstacle.x, obstacle.y],
            [obstacle.x, obstacle.y + obstacle.depth],
            [obstacle.x + obstacle.width, obstacle.y + obstacle.depth],
            [obstacle.x + obstacle.width, obstacle.y],
            [obstacle.x, obstacle.y],
          ],
        ]);
        return p;
      });
      const spacingPolygons = [
        polygon([
          [
            [0, 0],
            [0, localHangar?.depth || 0],
            [localHangar?.wall_spacing_left || 0, localHangar?.depth || 0],
            [localHangar?.wall_spacing_left || 0, 0],
            [0, 0],
          ],
        ]),
        polygon([
          [
            [localHangar?.width || 0, 0],
            [localHangar?.width || 0, localHangar?.depth || 0],
            [
              (localHangar?.width || 0) -
                (localHangar?.wall_spacing_right || 0),
              localHangar?.depth || 0,
            ],
            [
              (localHangar?.width || 0) -
                (localHangar?.wall_spacing_right || 0),
              0,
            ],
            [localHangar?.width || 0, 0],
          ],
        ]),
        polygon([
          [
            [0, localHangar?.depth || 0],
            [localHangar?.width || 0, localHangar?.depth || 0],
            [
              localHangar?.width || 0,
              (localHangar?.depth || 0) - (localHangar?.wall_spacing_back || 0),
            ],
            [
              0,
              (localHangar?.depth || 0) - (localHangar?.wall_spacing_back || 0),
            ],
            [0, localHangar?.depth || 0],
          ],
        ]),
      ].filter((x) => false);

      const polygonsToUnion = [...obstaclePolygons, ...spacingPolygons];
      const unionPolygon =
        polygonsToUnion.length <= 1
          ? polygonsToUnion[0]
          : union(featureCollection(polygonsToUnion));

      if (!unionPolygon) {
        console.info("No union polygon");
        return grossSquareFootage;
      }
      const obstacleSquareFootage = calculateGeometryArea(
        unionPolygon.geometry
      );
      return grossSquareFootage - obstacleSquareFootage;
    } catch (e) {
      console.error(e);
      // if all else fails just return the gross square footage
      return grossSquareFootage;
    }
  }, [localHangar]);

  if (!localHangar) {
    return null;
  }

  return (
    <>
      {/* Tab 0: Basic Info */}
      <TabPanel value={activeTab} index={0}>
        <Stack
          direction="column"
          spacing={2}
          sx={{ width: "100%", maxWidth: "100%" }}
        >
          <TextField
            fullWidth
            size="small"
            label="Hangar Name"
            onChange={(evt) =>
              setLocalHangar({ ...localHangar, name: evt.target.value })
            }
            value={localHangar?.name ?? ""}
          />
        </Stack>
      </TabPanel>

      {/* Tab 1: Dimensions */}
      <TabPanel value={activeTab} index={1}>
        <Stack
          direction="column"
          spacing={2}
          sx={{ width: "100%", maxWidth: "100%" }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <MetricAwareTextField
              unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
              disabled={activeFBO?.subscription === "standard"}
              type="number"
              fullWidth
              InputProps={{ inputProps: { min: 50, max: 600 } }}
              size="small"
              label={`Width (${unit})`}
              onChange={(value) =>
                setLocalHangar({
                  ...(localHangar || {}),
                  width: Math.min(600, value),
                })
              }
              value={localHangar?.width}
            />
            <MetricAwareTextField
              unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
              disabled={activeFBO?.subscription === "standard"}
              type="number"
              fullWidth
              InputProps={{ inputProps: { min: 50, max: 600 } }}
              size="small"
              label={`Depth (${unit})`}
              onChange={(value) =>
                setLocalHangar({
                  ...(localHangar || {}),
                  depth: Math.min(600, value),
                })
              }
              value={localHangar?.depth}
            />
          </Stack>
          <Stack direction="row" justifyContent="space-around">
            <Stack direction="column" spacing={1} alignItems="center">
              <Typography>Gross Square Footage</Typography>
              <Typography>
                {formatUnit(
                  localHangar?.width * localHangar?.depth,
                  true,
                  activeFBO?.settings?.useMetricSystem
                )}
              </Typography>
            </Stack>
            <Stack direction="column" spacing={1} alignItems="center">
              <Typography>Usable Square Footage</Typography>
              <Typography>
                {formatUnit(
                  usableSquareFootage,
                  true,
                  activeFBO?.settings?.useMetricSystem
                )}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="column" spacing={1}>
            <Typography variant="body2">Door Pockets</Typography>
            <Stack direction="row" spacing={2}>
              <MetricAwareTextField
                unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
                disabled={activeFBO?.subscription === "standard"}
                type="number"
                fullWidth
                InputProps={{ inputProps: { min: 0, max: 200 } }}
                size="small"
                label={`Left Door Length (${unit})`}
                onChange={(value) =>
                  setLocalHangar({
                    ...(localHangar || {}),
                    left_door: value,
                  })
                }
                value={localHangar?.left_door}
              />
              <MetricAwareTextField
                unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
                disabled={activeFBO?.subscription === "standard"}
                type="number"
                fullWidth
                InputProps={{ inputProps: { min: 0, max: 200 } }}
                size="small"
                label={`Right Door Length (${unit})`}
                onChange={(value) =>
                  setLocalHangar({
                    ...(localHangar || {}),
                    right_door: value,
                  })
                }
                value={localHangar?.right_door}
              />
            </Stack>
          </Stack>
          <Stack direction="column" spacing={1}>
            <Typography variant="body2">Door Dimensions</Typography>
            <Stack direction="row" spacing={2}>
              <MetricAwareTextField
                unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
                disabled={true}
                type="number"
                fullWidth
                InputProps={{ inputProps: { min: 1, max: 200 } }}
                size="small"
                label={`Width (${unit})`}
                value={
                  localHangar?.width -
                  (localHangar?.left_door + localHangar?.right_door)
                }
                onChange={() => {}}
              />
            </Stack>
          </Stack>
        </Stack>
      </TabPanel>

      {/* Tab 2: Height & Bays */}
      <TabPanel value={activeTab} index={2}>
        <Stack
          direction="column"
          spacing={2}
          sx={{ width: "100%", maxWidth: "100%" }}
        >
          <Stack direction="column" spacing={1}>
            <Typography variant="body2">Height Configuration</Typography>

            {/* Bay Configuration Toggle with inline info icon */}
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={useBayHeights}
                    onChange={(e) =>
                      onBayConfigChange?.({
                        useBayHeights: e.target.checked,
                        bayDefinitions,
                        bayOrientation,
                      })
                    }
                  />
                }
                label="Use bay-specific heights"
              />
              <Tooltip title="Enable for stepped-eave hangars with different height zones. Each bay can have its own eave and clear height.">
                <InfoOutlinedIcon fontSize="small" color="action" />
              </Tooltip>
            </Stack>

            {/* Conditional: Single Height or Bay Editor */}
            {!useBayHeights ? (
              // Single height field for uniform hangars
              <Stack direction="row" spacing={1} alignItems="center">
                <MetricAwareTextField
                  unit={
                    activeFBO?.settings?.useMetricSystem ? "meters" : "feet"
                  }
                  disabled={activeFBO?.subscription === "standard"}
                  type="number"
                  fullWidth
                  InputProps={{ inputProps: { min: 1, max: 200 } }}
                  size="small"
                  label={`Height (${unit})`}
                  onChange={(value) =>
                    setLocalHangar({
                      ...(localHangar || {}),
                      height: value,
                    })
                  }
                  value={localHangar?.height}
                />
                <Tooltip title="Eave height for uniform hangars. This is the vertical clearance from floor to the lowest point of the roof structure.">
                  <InfoOutlinedIcon
                    fontSize="small"
                    color="action"
                    sx={{ mt: 1 }}
                  />
                </Tooltip>
              </Stack>
            ) : (
              // Bay editor for stepped-eave hangars
              <Box sx={{ maxWidth: 600 }}>
                <HangarBayEditor
                  hangarWidth={localHangar?.width || 0}
                  hangarDepth={localHangar?.depth || 0}
                  bayDefinitions={bayDefinitions}
                  bayOrientation={bayOrientation}
                  onChange={(bays, orientation) => {
                    onBayConfigChange?.({
                      useBayHeights,
                      bayDefinitions: bays,
                      bayOrientation: orientation,
                    });
                  }}
                />
              </Box>
            )}
          </Stack>
        </Stack>
      </TabPanel>

      {/* Tab 3: Features */}
      <TabPanel value={activeTab} index={3}>
        <Stack
          direction="column"
          spacing={2}
          sx={{ width: "100%", maxWidth: "100%" }}
        >
          {hideGarageDoors ? (
            <div />
          ) : (
            <Stack direction="column" spacing={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2">Garage Doors</Typography>
                <Button
                  disabled={activeFBO?.subscription === "standard"}
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setLocalHangar({
                      ...(localHangar || {}),
                      garageDoors: localHangar?.garageDoors?.concat([
                        {
                          id: uuidv4(),
                          fbo_id: activeFBO.id,
                          hangar_id: localHangar?.id,
                          wall: "left",
                          x: 0,
                          y: 0,
                          width: 10,
                        },
                      ]),
                    });
                  }}
                >
                  Add
                </Button>
              </Stack>
              {(localHangar?.garageDoors?.length || 0) === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: "center" }}
                >
                  No garage doors. Click Add to create one.
                </Typography>
              ) : (
                localHangar?.garageDoors?.map((garageDoor, idx) => (
                  <Stack key={`garage-door-${idx}`} direction="row" spacing={2}>
                    <TextField
                      disabled={activeFBO?.subscription === "standard"}
                      select
                      size="small"
                      label="Wall"
                      fullWidth
                      onChange={(evt) => {
                        const wall = evt.target.value;
                        const x =
                          wall === "back"
                            ? garageDoor.x
                            : wall === "right"
                            ? localHangar?.width
                            : 0;
                        const y =
                          wall !== "back" ? garageDoor.y : localHangar?.depth;
                        setIGarageDoor(idx, {
                          ...garageDoor,
                          wall,
                          x,
                          y,
                        });
                      }}
                      value={garageDoor.wall}
                    >
                      <MenuItem value="left">Left</MenuItem>
                      <MenuItem value="back">Back</MenuItem>
                      <MenuItem value="right">Right</MenuItem>
                    </TextField>

                    <MetricAwareTextField
                      unit={
                        activeFBO?.settings?.useMetricSystem ? "meters" : "feet"
                      }
                      disabled={activeFBO?.subscription === "standard"}
                      type="number"
                      fullWidth
                      inputProps={{
                        min: 0,
                        max:
                          garageDoor.wall === "back"
                            ? localHangar?.width || 0
                            : localHangar?.depth || 0,
                        step,
                      }}
                      size="small"
                      onChange={(width) => {
                        setIGarageDoor(idx, {
                          ...garageDoor,
                          width,
                        });
                      }}
                      value={garageDoor.width}
                      label="Width"
                    />
                    <IconButton
                      disabled={activeFBO?.subscription === "standard"}
                      color="error"
                      onClick={() =>
                        setLocalHangar({
                          ...(localHangar || {}),
                          garageDoors: localHangar?.garageDoors?.filter(
                            (x, i) => i !== idx
                          ),
                        })
                      }
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Stack>
                ))
              )}
            </Stack>
          )}
          <Stack direction="column" spacing={1}>
            <Typography variant="body2">Obstacles</Typography>
            <ObstacleToolbar
              hangar={localHangar}
              onAdd={(obstacle) =>
                setLocalHangar({
                  ...localHangar,
                  obstacles: [...(localHangar.obstacles || []), obstacle],
                })
              }
            />
          </Stack>
          <Stack direction="column" spacing={1}>
            <Typography variant="body2">Wall Spacing</Typography>
            <Stack direction="row" spacing={2}>
              <MetricAwareTextField
                unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
                type="number"
                fullWidth
                inputProps={{ min: 0, max: 15, step }}
                size="small"
                label={`Left Wall (${unit})`}
                onChange={(value) =>
                  setLocalHangar({
                    ...(localHangar || {}),
                    wall_spacing_left: value,
                  })
                }
                value={localHangar?.wall_spacing_left}
              />
              <MetricAwareTextField
                unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
                type="number"
                fullWidth
                inputProps={{ min: 0, max: 15, step }}
                size="small"
                label={`Back Wall (${unit})`}
                onChange={(value) =>
                  setLocalHangar({
                    ...(localHangar || {}),
                    wall_spacing_back: value,
                  })
                }
                value={localHangar?.wall_spacing_back}
              />
              <MetricAwareTextField
                unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
                type="number"
                fullWidth
                inputProps={{ min: 0, max: 15, step }}
                size="small"
                label={`Right Wall (${unit})`}
                onChange={(value) =>
                  setLocalHangar({
                    ...(localHangar || {}),
                    wall_spacing_right: value,
                  })
                }
                value={localHangar?.wall_spacing_right}
              />
            </Stack>
          </Stack>
        </Stack>
      </TabPanel>
    </>
  );
};
